<template>
  <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-8">
    <div class="tw:py-6 tw:pl-6 tw:pr-6 tw:md:pr-0">
      <img :src="businessPlusLogo" alt="Business Studio+" class="tw:h-[16px] tw:mb-2" />
      <slot name="heading" />
      <p v-html="$t('rc_plus_upsell_subtext')"></p>
      <div class="tw:my-5 tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:auto-rows-fr tw:gap-3">
        <div v-for="item in featureTiles" :key="item.id" class="tw:flex tw:gap-2 tw:p-3 tw:rounded-xl tw:bg-blue-100">
          <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center">
            <img :src="item.icon" :alt="item.title" class="tw:w-[18px] tw:h-[18px] tw:shrink-0" />
          </div>
          <div>
            <p class="tw:font-bold tw:text-sm">{{ $t(item.title) }}</p>
            <p class="tw:text-sm">{{ $t(item.subtitle) }}</p>
          </div>
        </div>
      </div>
      <div class="tw:p-3 tw:rounded-lg tw:bg-green-100">
        <p class="tw:text-sm tw:font-bold tw:mb-1">{{ $t('rc_plus_upsell_discount_header') }}</p>
        <div class="tw:mb-1">
          <i18n-t
            v-if="!isPriceFetching"
            keypath="rc_plus_upsell_discounted_price"
            tag="div"
            class="tw:flex tw:items-center tw:gap-1 tw:text-gray-700 tw:text-xs">
            <template #DiscountedPrice>
              <span class="tw:font-bold tw:text-green-700 tw:text-2xl">{{
                formatCurrency(discountedMonthlyPrice)
              }}</span>
            </template>
            <template #OriginalPrice>
              <span class="tw:line-through tw:text-gray-700 tw:text-2xl">{{ formatCurrency(monthlyPrice) }}</span>
            </template>
          </i18n-t>
          <SmallDotLoader v-else color="#B4183A" />
        </div>
        <i18n-t
          v-if="!isPriceFetching"
          keypath="rc_plus_upsell_price_info"
          tag="div"
          class="tw:text-xs tw:text-gray-600 tw:flex tw:gap-0.5">
          <template #DiscountedPrice>
            <span>{{ formatCurrency(totalPriceBeforeTaxes) }}</span>
          </template>
          <template #OriginalPrice>
            <span class="tw:line-through">{{ formatCurrency(baseSubscriptionFullTermPrice) }}</span>
          </template>
        </i18n-t>
      </div>
    </div>
    <div>
      <RateConnectPlusCrossSellBannerAnimation class="tw:hidden tw:md:block" />
    </div>
  </div>
</template>

<script setup lang="ts">
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import RateConnectPlusCrossSellBannerAnimation from '@/components/rateConnect/studioPlusCrossSell/RateConnectPlusCrossSellBannerAnimation.vue'
import { useFormatting } from '@/composables/useFormatting'

interface Props {
  isPriceFetching: boolean
  discountedMonthlyPrice: number
  monthlyPrice: number
  totalPriceBeforeTaxes: number
  baseSubscriptionFullTermPrice: number
}

defineProps<Props>()

const HotelConnect = new URL('/src/assets/img/studio-plus/icons/HotelConnect.svg', import.meta.url).href
const RateInsights = new URL('/src/assets/img/studio-plus/icons/RateInsights.svg', import.meta.url).href
const TravelerTrends = new URL('/src/assets/img/studio-plus/icons/TravelerTrends.svg', import.meta.url).href
const HotelOffers = new URL('/src/assets/img/studio-plus/icons/HotelOffersIcon.svg', import.meta.url).href
const businessPlusLogo = new URL('/src/assets/img/logos/business-plus-logo.svg', import.meta.url).href

const { formatCurrency } = useFormatting()

const featureTiles = [
  {
    id: 'hotel-offers',
    title: 'hotel_offers_feature',
    subtitle: 'rc_plus_upsell_hotel_offers_subtext',
    icon: HotelOffers,
  },
  {
    id: 'rate-insights',
    title: 'rate_insights_feature',
    subtitle: 'rc_plus_upsell_rate_insights_subtext',
    icon: RateInsights,
  },
  {
    id: 'visitors-profile',
    title: 'visitors_profile_feature',
    subtitle: 'rc_plus_upsell_visitors_profile_subtext',
    icon: TravelerTrends,
  },
  {
    id: 'hotel-connect',
    title: 'hotel_connect_feature',
    subtitle: 'rc_plus_upsell_hotel_connect_subtext',
    icon: HotelConnect,
  },
]
</script>
